import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '../utils/cn';
import { formatCurrency } from '../utils/format';
import type { QuoteSnapshot } from '../wizard/types';

interface ValueBarProps {
  quote: QuoteSnapshot | null;
  /** Optional label rendered to the left of the figures (e.g. "Sending"). */
  prefix?: string;
  className?: string;
}

/**
 * Sticky bottom value bar shown during the Collect phase. Carries the
 * promise of value ("Sending $500 → 9.210 MXN · Fee $4.99") so the user
 * never loses sight of what they're agreeing to while filling PII.
 *
 * Mobile keyboard caveat: iOS Safari overlays the keyboard on top of fixed
 * elements (no viewport resize), which makes the bar collide with input
 * fields. We hide the bar whenever an input/textarea is focused — focus
 * blur restores it. This is more reliable than listening to
 * `visualViewport.resize` because the latter is inconsistent across
 * browsers and embeds.
 */
export const ValueBar = ({ quote, prefix, className }: ValueBarProps) => {
  const { t } = useTranslation('smart');
  const [inputFocused, setInputFocused] = useState(false);

  useEffect(() => {
    const isFormField = (el: EventTarget | null): boolean => {
      if (!(el instanceof HTMLElement)) return false;
      const tag = el.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
      return el.isContentEditable;
    };
    const onFocusIn = (e: FocusEvent) => {
      if (isFormField(e.target)) setInputFocused(true);
    };
    const onFocusOut = (e: FocusEvent) => {
      if (isFormField(e.target)) setInputFocused(false);
    };
    window.addEventListener('focusin', onFocusIn);
    window.addEventListener('focusout', onFocusOut);
    return () => {
      window.removeEventListener('focusin', onFocusIn);
      window.removeEventListener('focusout', onFocusOut);
    };
  }, []);

  if (!quote) return null;

  return (
    <AnimatePresence initial={false}>
      {!inputFocused && (
        <motion.div
          key="valuebar"
          initial={{ y: 32, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 32, opacity: 0 }}
          transition={{ duration: 0.18, ease: [0.22, 0.61, 0.36, 1] }}
          className={cn(
            'sticky bottom-0 left-0 right-0 z-30',
            // pb-[env(safe-area-inset-bottom)] respects iOS home indicator.
            'pb-[env(safe-area-inset-bottom)]',
            className,
          )}
        >
          <div className="border-border bg-bg-elevated/90 backdrop-blur-md rounded-2xl border px-4 py-3 shadow-lg">
            <div className="flex items-center justify-between gap-3 text-sm">
              <div className="flex flex-col leading-tight">
                {prefix && (
                  <span className="text-fg-muted text-[10px] font-semibold uppercase tracking-wide">
                    {prefix}
                  </span>
                )}
                <span className="text-fg text-base font-semibold tabular-nums">
                  {formatCurrency(
                    quote.sendAmount + quote.totalFee,
                    quote.sendCurrency,
                  )}
                  <span className="text-fg-muted mx-2 text-xs" aria-hidden>
                    →
                  </span>
                  <span className="text-accent">
                    {formatCurrency(quote.receiveAmount, quote.receiveCurrency)}
                  </span>
                </span>
              </div>
              <div className="text-right text-[11px]">
                <div className="text-fg-muted">{t('wizard.valueBar.fee')}</div>
                <div className="text-fg font-semibold tabular-nums">
                  {formatCurrency(quote.totalFee, quote.sendCurrency)}
                </div>
              </div>
            </div>
            {quote.estimated && (
              <p className="text-fg-muted mt-1 text-[10px]">
                {t('wizard.valueBar.estimatedNote')}
              </p>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
